import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import { SeatingItemPropertyMap } from "@shared/modules/seating/types/seating-property-types";
import {
  MapPermissionsType,
  SeatingSelectReturnType,
  LocationDataType,
} from "@shared/modules/seating/types/seating-types";
import { useCallback, useMemo, useState } from "react";
import { TicketItemGroupType } from "@shared/modules/item-groups/types/ticket-item-group-type";
import useAssignedSeatingHelpers from "@shared/hooks/seating/useAssignedSeatingHelpers/useAssignedSeatingHelpers";
import {
  DefaultLocationSelectModalProps,
  MapItemType,
} from "./DefaultLocationSelect.props";

export function useDefaultLocationSelectModal(
  props: DefaultLocationSelectModalProps & {
    locationSelections: Array<SeatingSelectReturnType>;
    updateLocationSelections: (input: Array<SeatingSelectReturnType>) => void;
  }
) {
  const {
    itemsOnSeat,
    seatingEvent,
    ticketAuthArray,
    locationSelections,
    onClose,
    updateLocationSelections,
  } = props;
  const { $permissions } = itemsOnSeat;

  const {
    seatingServiceState,
    getInfoRequiredLocationQtyInCart,
    getLocationQtyInCart,
  } = useSeatingState();

  const { requestUpdateLocationPermissions } = useAssignedSeatingHelpers();

  // Public basket is the default type
  const {
    itemGroups,
    areAllRenewalItemGroups,
    eachItemGroupHasSeatOrLocationPermission,
  } = useBasketService();

  const [screen, setScreen] = useState<"select-item" | "select-customer">(
    "select-item"
  );

  const handleScreen = (screen: "select-item" | "select-customer") => {
    setScreen(screen);
  };

  const [customerSelected, setCustomerSelected] =
    useState<TicketItemGroupType | null>(null);

  const handleSelectCustomer = (itemGroup: TicketItemGroupType) => {
    setCustomerSelected(itemGroup);
  };

  const handleUnselectCustomer = () => {
    setCustomerSelected(null);
  };

  const getCurrentQtyInBasketLocation = useCallback(
    (itemId: number, permissionId: number) => {
      if (seatingServiceState.getCartPermission(permissionId)) {
        // info_required items with locations are split into different item_groups
        // the quantity will be the number of item_groups with the same id value
        const itemGroup = itemGroups?.find(
          (ig) => ig.type === itemId || ig.membership_level === itemId
        );

        if (itemGroup?.info_required) {
          return getInfoRequiredLocationQtyInCart(itemId, permissionId, 0);
        }

        return getLocationQtyInCart(itemId, permissionId, 0);
      }

      return 0;
    },
    [
      itemGroups,
      seatingServiceState,
      getInfoRequiredLocationQtyInCart,
      getLocationQtyInCart,
    ]
  );

  // get the current selected amounts for each item type
  // either from the basket or from the locationSelections array
  const getCurrentSelectedAmount = useCallback(
    (
      item: SeatingItemPropertyMap & {
        permissionId: number;
      }
    ): number => {
      if (
        !locationSelections.length ||
        !locationSelections.find((loc) => loc.itemTypeId === item.id)
      ) {
        return getCurrentQtyInBasketLocation(item.id, item.permissionId) ?? 0;
      }
      return (
        locationSelections.find((loc) => loc.itemTypeId === item.id)
          ?.requestAmount ?? 0
      );
    },
    [locationSelections, getCurrentQtyInBasketLocation]
  );

  const selectItemPermission = useCallback(
    ({
      itemTypeId,
      permissionId,
      minPurchaseLimit,
      requestAmount,
      pwycPrice,
    }: {
      itemTypeId: number;
      permissionId: number;
      minPurchaseLimit?: number;
      requestAmount?: number;
      pwycPrice?: string | null;
    }) => {
      if (itemsOnSeat) {
        const returnData = {
          itemTypeId,
          permissionId,
          requestAmount,
          ticketAuthArray,
          minPurchaseLimit,
          payWhatYouCanPrice: pwycPrice,
        };

        const newLocationSelections = locationSelections
          .filter((selection) => selection.itemTypeId !== returnData.itemTypeId)
          .concat([returnData]);
        updateLocationSelections(newLocationSelections);
      }
    },
    [ticketAuthArray, itemsOnSeat, locationSelections, updateLocationSelections]
  );

  // item cards displayed on the selection modal
  const locationItems: Array<MapItemType> = useMemo(() => {
    return $permissions.map((permission: MapPermissionsType) => {
      const cartLocationPermission = seatingServiceState.getCartPermission(
        Number(permission.seatingItemPermissionId)
      );
      const itemType: SeatingItemPropertyMap =
        seatingServiceState?.itemTypeMap[
          permission.seatingItemTypeId as keyof typeof seatingServiceState.itemTypeMap
        ];
      const requestAmount =
        (cartLocationPermission && cartLocationPermission?.location?.count) ||
        0;

      const usageRemaining =
        Number(permission?.usage_remaining) + requestAmount;

      const $countOptions =
        itemType?.$countOptions?.filter(
          (option: number) => option <= usageRemaining
        ) ?? [];

      return {
        ...itemType,
        $countOptions,
        permissionId: Number(permission.seatingItemPermissionId),
        requestAmount,
        usageRemaining,
      };
    });
  }, [$permissions, seatingServiceState]);

  const cancelModal = () => {
    onClose(null);
  };

  const handleDoneClick = () => {
    requestUpdateLocationPermissions(
      locationSelections.map((location) => ({
        itemTypeId: location.itemTypeId,
        permissionId: location.permissionId,
        qty: 1,
      })),
      itemsOnSeat.$data as LocationDataType,
      itemGroups.find((group) => group.id === customerSelected?.id)?.id
    );
    if (eachItemGroupHasSeatOrLocationPermission) {
      props.resolve(locationSelections);
    } else {
      onClose(null);
    }
  };

  return {
    locationItems,
    selectItemPermission,
    getCurrentSelectedAmount,
    cancelModal,
    screen,
    handleScreen,
    customerSelected,
    handleSelectCustomer,
    handleUnselectCustomer,
    areAllRenewalItemGroups,
    eachItemGroupHasSeatOrLocationPermission,
    seatingEvent,
    itemGroups,
    handleDoneClick,
  };
}
